import { RestError } from '@azure/core-http';
import { KnownNoteAttachedToDtoType } from '@groundfloor-platform/mgmt-service-g-sdk';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useImmer } from 'use-immer';
import { castDraft } from 'immer';
import { naturalSort } from '../../../../../core/util/natural-sort.util';
import { DropdownOption } from '../../../../../ui/components/pure/dropdown/dropdown.component';
import { useQueryGetCarrier, useQueryGetCarrierHierarchy } from '../../../../carriers/carriers.queries';
import { useMutationCreateNote } from '../../../../notes/notes.queries';
import { useMutationCreateCourier } from '../../../couriers.queries';
import {
  CreateCourierFormState,
  LoginTab,
  loginTabForMethod,
  makeCreateCourierFormConfig,
  mapCourierFormToCreateCourierDto,
  resolveCourierDestination,
  resolveCourierGroupID,
  resolveLoginMethodGroupID
} from '../../../logic/courier-form.map';
import { AddCourierActionProps } from './add-courier-action.component';

export type AddCourierActionState = {
  saving: boolean;
  error?: RestError;
};

export interface AddCourierActionHandlers {
  onCancel(): void;
  onSubmit(form: CreateCourierFormState): void;
}

export function useCreateCourierAction({ projectID, carrier, onAbort, onSuccess }: AddCourierActionProps) {
  const [state, setState] = useImmer<AddCourierActionState>({ saving: false });

  const form = useCreateCourierForm(projectID, carrier);

  const createCourierMutation = useMutationCreateCourier();
  const createNoteMutation = useMutationCreateNote();

  const handlers: AddCourierActionHandlers = {
    onCancel: onAbort,
    onSubmit(formValues) {
      setState((draft) => {
        draft.saving = true;
      });

      const groupID = resolveCourierGroupID(formValues);
      if (!groupID) {
        setState((draft) => {
          draft.saving = false;
        });
        return;
      }

      const dto = mapCourierFormToCreateCourierDto(formValues, groupID);
      createCourierMutation.mutate(dto, {
        onSuccess(courier) {
          if (!formValues.notes) {
            onSuccess();
            return;
          }
          // Notes have no home on CreateCourierDto — they're created via the generic
          // per-user notes endpoint once the courier itself exists.
          createNoteMutation.mutate(
            {
              nodeType: KnownNoteAttachedToDtoType.User,
              nodeId: courier.id,
              content: formValues.notes as string,
              isPrivate: false
            },
            {
              onSuccess() {
                onSuccess();
              },
              onError() {
                onSuccess();
              }
            }
          );
        },
        onError(error) {
          setState((draft) => {
            draft.saving = false;
            draft.error = castDraft(error);
          });
        }
      });
    }
  };

  return { state, handlers, form } as const;
}

export function useCreateCourierForm(projectID: string, carrier?: AddCourierActionProps['carrier']) {
  const { watch, control, handleSubmit, setValue } = useForm<CreateCourierFormState>();

  const [activeLoginTab, setActiveLoginTab] = useState<LoginTab>('mobile');

  // One request describes the whole form. Any project in the hierarchy answers identically, so ask
  // with the one the user is scoped to.
  const hierarchy = useQueryGetCarrierHierarchy(projectID, { enabled: !carrier });

  const units = useMemo(() => hierarchy.data?.units ?? [], [hierarchy.data]);
  const unitMenuItems: DropdownOption<string>[] = useMemo(
    () => naturalSort(units.map((unit) => ({ label: unit.name, value: unit.id }))).asc((option) => option.label),
    [units]
  );

  // A label, not a choice: a project belongs to one company, and `organisation` is display-only. A
  // carrier in no company has none, so the anchor above it names the form.
  const carrierLabel = carrier?.name ?? hierarchy.data?.organisation?.name ?? hierarchy.data?.anchorProject?.name;

  // The unit this project runs, or the container to resolve when it runs none — see
  // `resolveCourierDestination`. Preselected either way, so the picker opens on it.
  const { groupID: resolvedGroupID, showUnitPicker } = carrier
    ? { groupID: carrier.id, showUnitPicker: false }
    : resolveCourierDestination(hierarchy.data);
  useEffect(() => {
    if (resolvedGroupID) {
      setValue('groupID', resolvedGroupID);
    }
  }, [resolvedGroupID, setValue]);

  // A carrier group answers with the method it holds or inherits; a container answers nothing, so
  // the unit it would resolve to is asked in its place.
  const groupID = watch('groupID');
  const carrierQuery = useQueryGetCarrier(resolveLoginMethodGroupID(hierarchy.data, groupID));
  useEffect(() => {
    setActiveLoginTab(loginTabForMethod(carrierQuery.data?.loginMethod ?? hierarchy.data?.organisation?.loginMethod));
  }, [carrierQuery.data, hierarchy.data]);

  const formConfig = makeCreateCourierFormConfig(activeLoginTab);

  return {
    formConfig,
    handleSubmit,
    control,
    setValue,
    watch,
    activeLoginTab,
    setActiveLoginTab,
    carrierLabel,
    unitMenuItems,
    showUnitPicker,
    loading: !carrier && hierarchy.isLoading && carrierQuery.isLoading
  };
}
